'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import {
    ComponentPropsWithoutRef,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    useRef,
    ReactNode,
} from 'react';
import { useWatch } from 'react-hook-form';
import { useCartStore, CartContext, getPriceForItem, getPriceValueTranslation } from '@/entities/cart';
import { isDeliveryMethod } from '@/entities/venue/lib/isDeliveryMethod';
import { useDeliveryMethodAvailability } from '@/entities/venue/lib/useDeliveryMethodAvailability';
import { CurrentVenueStatus } from '@/entities/venue/ui/current-venue-status';
import { AddressSelect } from '@/features/address/ui/address-select';
import { QueryApi } from '@/features/query/api';
import { cn } from '@/shared/lib/cn';
import { useSelectedCurrency } from '@/shared/lib/hooks/useSelectedCurrency';
import { Address } from '@/shared/model/Address';
import { Country } from '@/shared/model/Country';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Form } from '@/shared/ui/form';
import { Icon } from '@/shared/ui/icon';
import * as Tabs from '@/shared/ui/tabs';
import './cart-delivery-box.scss';
import { CartDeliveryMethodsList } from './cart-delivery-methods-list';

type Props = ComponentPropsWithoutRef<typeof Card>;
export const CartDeliveryBox = (props: Props) => {
    const t = useTranslations();
    const venueQuery = useQuery(QueryApi.Venue.queries.Venue);
    const deliveryMethodsQuery = useQuery(QueryApi.DeliveryMethods.queries.DeliveryMethods);
    const customerQuery = useQuery(QueryApi.Customer.queries.Customer);
    const addressesQuery = useQuery(QueryApi.Customer.queries.Addresses({ offset: 0, limit: 100 }));
    const [selectedCurrency] = useSelectedCurrency();
    const venueCountry = (venueQuery.data?.address?.country as Country) ?? Country.CZ;
    const getDeliveryMethodArea = useCartStore((s) => s.getDeliveryMethodArea);
    const getDeliveryMethodAreaPrice = useCartStore((s) => s.getDeliveryMethodAreaPrice);
    const { getAvailability, findFirstSelectable } = useDeliveryMethodAvailability();
    const { formMethods } = useContext(CartContext);
    const [isManual, addressId, deliveryMethodId, lat, lon] = useWatch({
        control: formMethods.control,
        name: [
            'deliveryMethod.manual',
            'deliveryMethod.addressId',
            'deliveryMethod.id',
            'deliveryMethod.address.lat',
            'deliveryMethod.address.lon',
        ],
    });

    const takeawayAddresses = useMemo(() => {
        const venue = venueQuery.data;
        if (!venue || !venue.address) return [];
        return [
            {
                id: venue.id ?? '',
                title: venue.name ?? '',
                address: venue.address as unknown as Address,
                icon: 'OTHER',
                isDefault: true,
                isEditable: false,
            },
        ];
    }, [venueQuery.data]);

    const isLoggedIn = !!customerQuery.data;
    const allAddresses = useMemo(
        () =>
            (addressesQuery.data?.items ?? []).map((item) => ({
                ...item,
                // The customer-address query carries the SDK `Timezone` enum while the UI
                // address types use the local `Timezone` enum (same field, divergent enum
                // declarations). The value is only ever passed through, so we reconcile the
                // structurally-identical shapes here.
                address: item.address as unknown as Address,
            })),
        [addressesQuery.data?.items],
    );

    const [deliveryMethods, pickupMethods] = useMemo(() => {
        const methods = deliveryMethodsQuery.data ?? [];
        const deliveryMethods: typeof methods = [];
        const pickupMethods: typeof methods = [];
        for (const method of methods) {
            if (isDeliveryMethod(method)) {
                deliveryMethods.push(method);
            } else {
                pickupMethods.push(method);
            }
        }
        return [deliveryMethods, pickupMethods];
    }, [deliveryMethodsQuery.data]);

    const hasDelivery = deliveryMethods.length > 0;
    const hasPickup = pickupMethods.length > 0;

    const handleDeliveryAddressChange = useCallback(
        (id: string | null, address?: Address | null) => {
            if (!id) {
                return;
            }
            formMethods.setValue('deliveryMethod.addressId', id);
            if (address) {
                // Use provided address (e.g., from onSuccess callback when creating new address)
                formMethods.setValue(
                    'deliveryMethod.address',
                    {
                        city: address.city,
                        country: address.country,
                        postCode: address.postCode,
                        street: address.street,
                        lat: address.lat!,
                        lon: address.lon!,
                    },
                    {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                    },
                );
            } else {
                // Find address in allAddresses (e.g., when selecting existing address)
                const addressItem = allAddresses.find((a) => a.id === id);
                if (addressItem) {
                    formMethods.setValue(
                        'deliveryMethod.address',
                        {
                            city: addressItem.address.city,
                            country: addressItem.address.country,
                            postCode: addressItem.address.postCode,
                            street: addressItem.address.street,
                            lat: addressItem.address.lat!,
                            lon: addressItem.address.lon!,
                        },
                        {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                        },
                    );
                }
            }
        },
        [allAddresses, formMethods],
    );

    const handleTakeawayAddressChange = useCallback((_: string | null) => {}, []);

    const derivedTab = useMemo(() => {
        if (deliveryMethods.some((m) => m.id === deliveryMethodId)) return 'delivery';
        if (pickupMethods.some((m) => m.id === deliveryMethodId)) return 'takeaway';
        return hasDelivery ? 'delivery' : 'takeaway';
    }, [deliveryMethodId, deliveryMethods, pickupMethods, hasDelivery]);

    const [activeTab, setActiveTab] = useState(derivedTab);

    useEffect(() => {
        setActiveTab(derivedTab);
    }, [derivedTab]);

    const handleTabChange = (value: string) => {
        setActiveTab(value);
        if (value === 'delivery') {
            const defaultAddress = allAddresses.find((a) => a.isDefault) ?? allAddresses[0];
            // Evaluate against the default address we're about to set; when there is
            // none, let the hook fall back to whatever address is already in the form.
            const parsedLat = parseFloat(defaultAddress?.address.lat ?? '');
            const parsedLon = parseFloat(defaultAddress?.address.lon ?? '');
            const coords =
                Number.isFinite(parsedLat) && Number.isFinite(parsedLon)
                    ? { lat: parsedLat, lon: parsedLon }
                    : undefined;
            const method = findFirstSelectable(deliveryMethods, coords);
            if (method) {
                formMethods.setValue('deliveryMethod.id', method.id);
            }
            if (defaultAddress) {
                handleDeliveryAddressChange(defaultAddress.id);
            }
        } else if (value === 'takeaway') {
            const method = findFirstSelectable(pickupMethods, null);
            if (method) {
                formMethods.setValue('deliveryMethod.id', method.id);
            }
            if (takeawayAddresses[0]) {
                handleTakeawayAddressChange(takeawayAddresses[0].id);
            }
        }
    };

    const shouldOpenAddressSelect = useRef(false);

    const handleSelectFromSaved = () => {
        shouldOpenAddressSelect.current = true;
        formMethods.setValue('deliveryMethod.manual', false);
        const deliveryAddress = formMethods.getValues('deliveryMethod.address');
        const matchingAddress = deliveryAddress?.street
            ? allAddresses.find(
                  ({ address }) =>
                      address.city === deliveryAddress.city &&
                      address.country === deliveryAddress.country &&
                      address.street === deliveryAddress.street &&
                      address.postCode === deliveryAddress.postCode &&
                      address.lat === deliveryAddress.lat &&
                      address.lon === deliveryAddress.lon,
              )
            : null;

        const newAddress = matchingAddress ?? allAddresses.find((a) => a.isDefault) ?? null;
        if (newAddress) {
            handleDeliveryAddressChange(newAddress?.id, newAddress.address);
        }
    };

    const handleEnterManually = () => {
        formMethods.setValue('deliveryMethod.manual', true);
        formMethods.setValue('deliveryMethod.addressId', null);
    };

    const selectedDeliveryMethod = deliveryMethods.find((m) => m.id === deliveryMethodId);
    const deliveryBadge = useMemo((): ReactNode => {
        const method = selectedDeliveryMethod ?? deliveryMethods.find((m) => !!m.areas?.length);
        if (!method) return undefined;
        const { taxedPrice, areas } = method;
        const hasAreas = !!areas?.length;
        const hasAddress = !!lat && !!lon;

        if (hasAreas && !hasAddress) {
            return (
                <Badge variant="error">
                    <Icon name="warning" />
                    {t('CART_DELIVERY_BOX.ADDRESS_REQUIRED')}
                </Badge>
            );
        }

        // Check area match separately from price — null area means truly outside delivery zone
        const matchedArea =
            hasAreas && hasAddress ? getDeliveryMethodArea(method, parseFloat(lat), parseFloat(lon)) : null;

        if (hasAreas && (matchedArea === null || matchedArea.deliveryAvailable === false)) {
            // A zone blocked only for now is not the same as no zone at all.
            const { areaTimeRestrictionLabel } = getAvailability(method);

            return (
                <Badge variant="error">
                    <Icon name="warning" />
                    {areaTimeRestrictionLabel ?? t('CART_DELIVERY_BOX.ADDRESS_UNDELIVERABLE')}
                </Badge>
            );
        }

        const areaPrice =
            hasAreas && hasAddress
                ? getDeliveryMethodAreaPrice(method, parseFloat(lat), parseFloat(lon))
                : null;

        const methodPrice = getPriceForItem({ prices: [taxedPrice] }, venueCountry, selectedCurrency);
        const totalPrice =
            (parseFloat(methodPrice?.amount || '0') ?? 0) + (parseFloat(areaPrice?.amount || '0') ?? 0);

        if (totalPrice) {
            return getPriceValueTranslation({ t, amount: totalPrice, currency: selectedCurrency });
        }

        return t('WORDS.FREE');
    }, [
        selectedDeliveryMethod,
        deliveryMethods,
        lat,
        lon,
        selectedCurrency,
        getDeliveryMethodArea,
        getDeliveryMethodAreaPrice,
        getAvailability,
        t,
    ]);

    useEffect(() => {
        if (activeTab !== 'delivery' || isManual) {
            shouldOpenAddressSelect.current = false;
        }
    }, [activeTab, isManual]);

    return (
        <Card {...props} className={cn('cart-delivery-box', props.className)} data-testid="cart-delivery-box">
            <h3 className="cart-delivery-box__title" data-testid="cart-delivery-box-title">
                {t('CART_DELIVERY_BOX.TITLE')}
            </h3>
            {!hasDelivery && !hasPickup ? (
                !deliveryMethodsQuery.isLoading && (
                    <p className="cart-delivery-box__no-methods" data-testid="cart-delivery-box-no-methods">
                        <Icon name="error" size={24} />
                        {t('CART_DELIVERY_BOX.NO_METHODS_AVAILABLE')}
                    </p>
                )
            ) : (
                <Tabs.Root value={activeTab} onValueChange={handleTabChange}>
                    <Tabs.List>
                        {hasDelivery && (
                            <Tabs.Trigger value="delivery" data-testid="cart-delivery-box-tab-delivery">
                                {t('CART_DELIVERY_BOX.TAB_DELIVERY')}
                            </Tabs.Trigger>
                        )}
                        {hasPickup && (
                            <Tabs.Trigger value="takeaway" data-testid="cart-delivery-box-tab-takeaway">
                                {t('CART_DELIVERY_BOX.TAB_TAKEAWAY')}
                            </Tabs.Trigger>
                        )}
                    </Tabs.List>
                    {hasDelivery && (
                        <Tabs.Content value="delivery" className="cart-delivery-box__content">
                            {!isLoggedIn || isManual ? (
                                <>
                                    <Form.AddressAutocompleteField
                                        name="deliveryMethod.address"
                                        fieldProps={{ label: t('CART_DELIVERY_BOX.YOUR_ADDRESS_LABEL') }}
                                        rules={{ validate: (value) => !!Object.keys(value ?? {}).length }}
                                        additionalButtons={
                                            isLoggedIn ? (
                                                <Button
                                                    variant="outlined"
                                                    className="address-select__button"
                                                    data-testid="address-select-select-from-saved-button"
                                                    onClick={handleSelectFromSaved}
                                                >
                                                    {t('CART_DELIVERY_BOX.SELECT_FROM_SAVED_BUTTON')}
                                                </Button>
                                            ) : undefined
                                        }
                                    />
                                    {deliveryBadge && (
                                        <div
                                            className="cart-delivery-box__address-status"
                                            data-testid="cart-delivery-box-address-status"
                                        >
                                            {deliveryBadge}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <label
                                        className="cart-delivery-box__label"
                                        data-testid="cart-delivery-box-address-label"
                                    >
                                        {t('CART_DELIVERY_BOX.YOUR_ADDRESS_LABEL')}
                                    </label>
                                    <AddressSelect
                                        triggerProps={{
                                            'data-testid': 'cart-delivery-box-delivery-address-select',
                                        }}
                                        addresses={allAddresses}
                                        isLoading={addressesQuery.isLoading}
                                        value={addressId ?? undefined}
                                        onValueChange={handleDeliveryAddressChange}
                                        onSuccess={handleDeliveryAddressChange}
                                        badge={deliveryBadge}
                                        defaultOpen={shouldOpenAddressSelect.current}
                                        additionalButtons={
                                            <Button
                                                variant="outlined"
                                                className="address-select__button"
                                                data-testid="address-select-enter-manually-button"
                                                onClick={handleEnterManually}
                                            >
                                                {t('CART_DELIVERY_BOX.ENTER_MANUALLY_BUTTON')}
                                            </Button>
                                        }
                                    />
                                </>
                            )}
                            {deliveryMethods.length > 1 && (
                                <>
                                    <label
                                        className="cart-delivery-box__label"
                                        data-testid="cart-delivery-box-delivery-method-label"
                                    >
                                        {t('CART_DELIVERY_BOX.DELIVERY_METHOD_LABEL')}
                                    </label>
                                    <CartDeliveryMethodsList methods={deliveryMethods} />
                                </>
                            )}
                        </Tabs.Content>
                    )}
                    {hasPickup && (
                        <Tabs.Content value="takeaway" className="cart-delivery-box__content">
                            <AddressSelect
                                triggerProps={{ 'data-testid': 'cart-delivery-box-takeaway-address-select' }}
                                addresses={takeawayAddresses}
                                isLoading={venueQuery.isLoading}
                                value={venueQuery.data?.id ?? undefined}
                                onValueChange={handleTakeawayAddressChange}
                                onSuccess={handleTakeawayAddressChange}
                                hideAddButton
                                disabled
                            />
                            <CurrentVenueStatus />
                            {pickupMethods.length > 1 && (
                                <>
                                    <label
                                        className="cart-delivery-box__label"
                                        data-testid="cart-delivery-box-pickup-method-label"
                                    >
                                        {t('CART_DELIVERY_BOX.PICKUP_METHOD_LABEL')}
                                    </label>
                                    <CartDeliveryMethodsList methods={pickupMethods} />
                                </>
                            )}
                        </Tabs.Content>
                    )}
                </Tabs.Root>
            )}
        </Card>
    );
};
